import type { AppDispatch } from '../../../store'
import store from '../../../store'
import { Security } from '../../../proto/generated/security'
import { selectActiveLayout, selectActiveLayoutId } from '../../../selectors/home'
import { selectAllFeatures, selectUserSubscriptionPlans, selectUserSubscriptionPlansData } from '../../../selectors/user'
import { hasFeatureAccess } from '../../../util/accessControl'
import { getSecurityKey } from '../../../util/security'
import {
    setActiveWidget,
    updateLayout,
    updateLayoutPanes,
    updateSyncSettings,
    updateWidgetSettings,
    updateWidgetType,
} from '../../../reducer/widgetDashboard.reducer'
import {
    generateLayoutConfig,
    generatePaneLayoutsMap,
    updatePanesToEqualSize,
} from '../../multiPane/multipPaneLayout.helper'
import type { MultiPaneItem, MultiPanesConfig } from '../../multiPane/multiPaneLayout.interface'
import { WidgetTypes } from '../../widgetDashboard/widgetDashboard.enums'
import type {
    DashboardSyncSettings,
    IWidgetDashboard,
    WidgetConfig,
    WidgetDashboardConfig,
} from '../../widgetDashboard/widgetDashboard.interface'
import {
    FeatureCategory,
    FeatureName,
    FeatureSubCategory,
} from '../../adminUserRestrictions/Restrictions.constants'
import {
    LAYOUT_PRESETS,
    detectPresetCode,
    findPresetByCode,
    leafPanesInVisualOrder,
    parseOrientation,
    resolveLayoutPreset,
    type LayoutOrientation,
} from './chartLayoutPresets'

export const LAYOUT_COMMAND_OPS = [
    'apply_layout',
    'split_pane',
    'close_pane',
    'focus_pane',
    'reset_layout',
    'equalize_panes',
    'set_layout_sync',
    'set_pane_widget_type',
    'set_pane_symbol',
    'set_layout_symbols',
] as const

/** Legacy alias kept so previously generated commands keep working. */
const OP_ALIASES: Record<string, string> = {
    split_layout: 'split_pane',
    delete_pane: 'close_pane',
    remove_pane: 'close_pane',
    select_pane: 'focus_pane',
    unsplit_layout: 'reset_layout',
    set_sync_settings: 'set_layout_sync',
}

export function normalizeLayoutOp(op: string): string {
    return OP_ALIASES[op] ?? op
}

export function isLayoutCommandOp(op: string): boolean {
    return (LAYOUT_COMMAND_OPS as readonly string[]).includes(normalizeLayoutOp(op))
}

export type LayoutCommandOutcome =
    | { status: 'applied'; detail?: string }
    | { status: 'noop'; detail?: string }
    | { status: 'failed'; reason: LayoutFailureReason; detail?: string }

export type LayoutFailureReason =
    | 'layout_unavailable'
    | 'pane_not_found'
    | 'unknown_layout'
    | 'restricted'
    | 'invalid_value'
    | 'last_pane'
    | 'security_not_found'
    | 'multistrike_requires_command'

export type LayoutCommandDeps = {
    dispatch: AppDispatch
    dashboard?: Pick<IWidgetDashboard, 'splitPane' | 'deletePane'>
    resolveSecurity: (exchange: unknown, code: unknown) => Promise<Security | undefined>
    setPrimarySecurity?: (
        key: string,
        securities: Security[],
        expressionMode: boolean,
        displayName?: string,
    ) => void
}

function shouldSyncPrimarySecurity(layout: WidgetDashboardConfig, slot: number): boolean {
    const slots = orderedPaneSlots(layout)
    if (slots.length <= 1) return true
    const active = layout.activeWidget
    return active != null ? active === slot : slot === slots[0]
}

const SYNC_KEY_ALIASES: Record<string, keyof DashboardSyncSettings> = {
    symbol: 'security',
    security: 'security',
    securities: 'security',
    crosshair: 'crosshair',
    cursor: 'crosshair',
    interval: 'timeBucket',
    timeframe: 'timeBucket',
    timebucket: 'timeBucket',
    time_bucket: 'timeBucket',
    time: 'time',
    drawing: 'drawing',
    drawings: 'drawing',
}

const WIDGET_TYPE_ALIASES: Record<string, WidgetTypes> = {
    chart: WidgetTypes.CHART,
    candle: WidgetTypes.CHART,
    candle_chart: WidgetTypes.CHART,
    candlestick: WidgetTypes.CHART,
    price: WidgetTypes.CHART,
    oi: WidgetTypes.OI_CHART,
    oi_chart: WidgetTypes.OI_CHART,
    open_interest: WidgetTypes.OI_CHART,
    multi_strike: WidgetTypes.MULTI_STRIKE_CHART,
    multi_strike_chart: WidgetTypes.MULTI_STRIKE_CHART,
    option_chain: WidgetTypes.OPTION_CHAIN,
    optionchain: WidgetTypes.OPTION_CHAIN,
}

const SELECTABLE_WIDGET_TYPES: WidgetTypes[] = [
    WidgetTypes.CHART,
    WidgetTypes.OI_CHART,
    WidgetTypes.MULTI_STRIKE_CHART,
    WidgetTypes.OPTION_CHAIN,
]

function resolveWidgetType(value: unknown): WidgetTypes | undefined {
    const raw = String(value ?? '').trim().toLowerCase().replace(/[\s-]+/g, '_')
    if (!raw) return undefined
    const alias = WIDGET_TYPE_ALIASES[raw]
    if (alias) return alias
    return SELECTABLE_WIDGET_TYPES.find((type) => type.toLowerCase() === raw)
}

function currentLayout(): { layoutId: string; layout: WidgetDashboardConfig | undefined } {
    const state = store.getState()
    return { layoutId: selectActiveLayoutId(state), layout: selectActiveLayout(state) }
}

function clonePaneTree(node: MultiPaneItem): MultiPaneItem {
    return JSON.parse(JSON.stringify(node)) as MultiPaneItem
}

function hasMultiPaneAccess(): boolean {
    const state = store.getState()
    const allFeatures = selectAllFeatures(state)
    const plans = selectUserSubscriptionPlans(state)
    const plansData = selectUserSubscriptionPlansData(state)
    const userFeatures = plans?.length
        ? plans.flatMap((plan: string) => plansData?.[plan] ?? [])
        : undefined
    return hasFeatureAccess(
        allFeatures,
        userFeatures,
        FeatureCategory.CHART,
        FeatureSubCategory.MULTIPANE,
        FeatureName.CHART_SPLIT,
    )
}

/**
 * Leaf pane slot ids in visual reading order. The agent addresses panes by their
 * 1-based position in this list, which is what a user means by "the second chart".
 */
export function orderedPaneSlots(layout: WidgetDashboardConfig | undefined): number[] {
    if (!layout?.panes) return []
    return leafPanesInVisualOrder(layout.panes).map((pane) => pane.id)
}

/**
 * Resolves a pane target. Accepts a 1-based index, a position keyword, or nothing
 * (meaning the currently focused pane).
 */
export function resolvePaneSlot(
    layout: WidgetDashboardConfig | undefined,
    target: unknown,
): number | undefined {
    const slots = orderedPaneSlots(layout)
    if (!slots.length) return undefined

    if (target == null || target === '') {
        const active = layout?.activeWidget
        return active != null && slots.includes(active) ? active : slots[0]
    }

    const numeric = Number(target)
    if (Number.isFinite(numeric)) {
        // Treat as a 1-based visual index; fall back to a direct slot id match.
        if (numeric >= 1 && numeric <= slots.length) return slots[numeric - 1]
        return slots.includes(numeric) ? numeric : undefined
    }

    const keyword = String(target).trim().toLowerCase().replace(/[\s_-]+/g, '')
    switch (keyword) {
        case 'first':
        case 'left':
        case 'top':
        case 'topleft':
        case 'lefttop':
            return slots[0]
        case 'second':
            return slots[1]
        case 'third':
            return slots[2]
        case 'fourth':
            return slots[3]
        case 'last':
        case 'right':
        case 'bottom':
        case 'bottomright':
        case 'rightbottom':
            return slots[slots.length - 1]
        case 'active':
        case 'focused':
        case 'current':
            return resolvePaneSlot(layout, undefined)
        default:
            return undefined
    }
}

function applyPreset(
    presetCode: string,
    deps: LayoutCommandDeps,
): LayoutCommandOutcome {
    const preset = findPresetByCode(presetCode)
    if (!preset?.wrapper.dashboardConfig) {
        return { status: 'failed', reason: 'unknown_layout' }
    }
    const { layoutId, layout } = currentLayout()
    if (!layoutId || !layout) {
        return { status: 'failed', reason: 'layout_unavailable' }
    }
    if (preset.paneCount > 1 && !hasMultiPaneAccess()) {
        return { status: 'failed', reason: 'restricted' }
    }
    // Already in this arrangement — reapplying would churn every chart for nothing.
    if (detectPresetCode(layout.panes) === preset.code) {
        return { status: 'noop', detail: preset.name }
    }

    const existingSlots = Object.keys(layout.widgets || {}).map(Number)
    if (!existingSlots.length) {
        return { status: 'failed', reason: 'layout_unavailable' }
    }
    const orderedExisting =
        layout.activeWidget != null && existingSlots.includes(layout.activeWidget)
            ? [layout.activeWidget, ...existingSlots.filter((slot) => slot !== layout.activeWidget)]
            : existingSlots

    // Mirrors predefinedPaneMenu: the preset remaps its pane ids onto the slots we
    // already have so existing charts survive the layout change.
    preset.wrapper.applyPreviousSlots(orderedExisting)
    const targetSlots = preset.wrapper.slots()
    const widgets: Record<number, WidgetConfig<any>> = {}
    const sourceSlots = orderedExisting.length ? orderedExisting : targetSlots

    targetSlots.forEach((slot, index) => {
        const sourceSlot = sourceSlots[index] ?? sourceSlots[0]
        const sourceWidget = layout.widgets?.[sourceSlot] ?? Object.values(layout.widgets || {})[0]
        if (!sourceWidget) return
        widgets[slot] = {
            ...sourceWidget,
            slotId: slot,
            settings: {
                ...sourceWidget.settings,
                chartId: `${layoutId}-${slot}`,
            },
        }
    })

    const activeWidget =
        layout.activeWidget != null && targetSlots.includes(layout.activeWidget)
            ? layout.activeWidget
            : targetSlots[0]

    deps.dispatch(
        updateLayout({
            id: layoutId,
            layout: {
                ...preset.wrapper.dashboardConfig,
                panes: clonePaneTree(preset.wrapper.dashboardConfig.panes),
                singleMode: preset.paneCount === 1,
                syncSettings: { ...layout.syncSettings },
                widgets,
                activeWidget,
            } as WidgetDashboardConfig,
        }),
    )
    return { status: 'applied', detail: preset.name }
}

function equalizeAllContainers(root: MultiPaneItem): MultiPaneItem {
    const tree = clonePaneTree(root)
    let panesConfig: MultiPanesConfig = generatePaneLayoutsMap(tree)
    Object.values(panesConfig)
        .filter((pane) => pane.panes.length > 1)
        .forEach((pane) => {
            panesConfig = updatePanesToEqualSize(pane.id, panesConfig)
        })
    return generateLayoutConfig(panesConfig, { ...panesConfig[tree.id] })
}

export async function applyLayoutCommand(
    rawCommand: Record<string, unknown>,
    deps: LayoutCommandDeps,
): Promise<LayoutCommandOutcome> {
    const op = normalizeLayoutOp(String(rawCommand.op || ''))
    const { layoutId, layout } = currentLayout()

    if (!layoutId || !layout) {
        return { status: 'failed', reason: 'layout_unavailable' }
    }

    switch (op) {
        case 'apply_layout': {
            const preset = resolveLayoutPreset({
                code: rawCommand.layout ?? rawCommand.code ?? rawCommand.preset,
                name: rawCommand.name,
                panes: rawCommand.panes,
                orientation: rawCommand.orientation,
            })
            if (!preset) return { status: 'failed', reason: 'unknown_layout' }
            return applyPreset(preset.code, deps)
        }

        case 'reset_layout': {
            if (orderedPaneSlots(layout).length <= 1) {
                return { status: 'noop', detail: 'Single pane' }
            }
            return applyPreset('LAYOUT_1', deps)
        }

        case 'split_pane': {
            if (!hasMultiPaneAccess()) {
                return { status: 'failed', reason: 'restricted' }
            }
            if (!deps.dashboard?.splitPane) {
                return { status: 'failed', reason: 'layout_unavailable' }
            }
            const slot = resolvePaneSlot(layout, rawCommand.pane)
            if (slot == null) return { status: 'failed', reason: 'pane_not_found' }

            const requested = Number(rawCommand.panes)
            const divisions = Number.isFinite(requested) ? Math.trunc(requested) : 2
            if (divisions < 2 || divisions > 4) {
                return { status: 'failed', reason: 'invalid_value' }
            }
            const orientation: LayoutOrientation = parseOrientation(rawCommand.orientation) ?? 'columns'
            deps.dashboard.splitPane(slot, divisions, orientation === 'columns')
            return { status: 'applied', detail: `${divisions} ${orientation}` }
        }

        case 'close_pane': {
            if (!deps.dashboard?.deletePane) {
                return { status: 'failed', reason: 'layout_unavailable' }
            }
            const slots = orderedPaneSlots(layout)
            if (slots.length <= 1) {
                return { status: 'failed', reason: 'last_pane' }
            }
            const slot = resolvePaneSlot(layout, rawCommand.pane)
            if (slot == null) return { status: 'failed', reason: 'pane_not_found' }
            deps.dashboard.deletePane(slot)
            return { status: 'applied', detail: `pane ${slots.indexOf(slot) + 1}` }
        }

        case 'focus_pane': {
            const slots = orderedPaneSlots(layout)
            const slot = resolvePaneSlot(layout, rawCommand.pane ?? rawCommand.position)
            if (slot == null) return { status: 'failed', reason: 'pane_not_found' }
            if (layout.activeWidget === slot) {
                return { status: 'noop', detail: `pane ${slots.indexOf(slot) + 1}` }
            }
            deps.dispatch(setActiveWidget({ id: layoutId, slotId: slot }))
            return { status: 'applied', detail: `pane ${slots.indexOf(slot) + 1}` }
        }

        case 'equalize_panes': {
            if (!layout.panes || orderedPaneSlots(layout).length <= 1) {
                return { status: 'noop' }
            }
            deps.dispatch(updateLayoutPanes({ id: layoutId, panes: equalizeAllContainers(layout.panes) }))
            return { status: 'applied' }
        }

        case 'set_layout_sync': {
            const requested = (rawCommand.sync ?? rawCommand.settings ?? rawCommand) as Record<string, unknown>
            const next: DashboardSyncSettings = { ...layout.syncSettings }
            const changed: string[] = []
            Object.entries(requested).forEach(([rawKey, rawValue]) => {
                const key = SYNC_KEY_ALIASES[rawKey.trim().toLowerCase()]
                if (!key || typeof rawValue !== 'boolean') return
                if (next[key] === rawValue) return
                next[key] = rawValue
                changed.push(`${rawKey} ${rawValue ? 'on' : 'off'}`)
            })
            if (!changed.length) {
                return { status: 'noop' }
            }
            deps.dispatch(updateSyncSettings({ id: layoutId, syncSettings: next }))
            return { status: 'applied', detail: changed.join(', ') }
        }

        case 'set_pane_widget_type': {
            const slot = resolvePaneSlot(layout, rawCommand.pane)
            if (slot == null) return { status: 'failed', reason: 'pane_not_found' }
            const widgetType = resolveWidgetType(rawCommand.widget_type ?? rawCommand.type)
            if (!widgetType) {
                return { status: 'failed', reason: 'invalid_value' }
            }
            if (widgetType === WidgetTypes.MULTI_STRIKE_CHART) {
                return { status: 'failed', reason: 'multistrike_requires_command' }
            }
            if (layout.widgets?.[slot]?.widgetType === widgetType) {
                return { status: 'noop', detail: widgetType }
            }
            deps.dispatch(updateWidgetType({ id: layoutId, slotId: slot, type: widgetType }))
            return { status: 'applied', detail: widgetType }
        }

        case 'set_pane_symbol': {
            const slots = orderedPaneSlots(layout)
            const slot = resolvePaneSlot(layout, rawCommand.pane)
            if (slot == null) return { status: 'failed', reason: 'pane_not_found' }
            const security = await deps.resolveSecurity(rawCommand.exchange, rawCommand.code ?? rawCommand.symbol)
            if (!security) return { status: 'failed', reason: 'security_not_found' }

            const key = getSecurityKey(security.exchange, security.code)
            window.securityManager?.setSecurity(security)
            deps.dispatch(
                updateWidgetSettings({
                    id: layoutId,
                    slotId: slot,
                    settings: {
                        key,
                        expressionMode: false,
                        securities: [key],
                        displayName: security.displayName || security.symbol,
                    },
                }),
            )
            const label = security.displayName || security.symbol || security.code
            const syncPrimary = shouldSyncPrimarySecurity(layout, slot)
            if (deps.setPrimarySecurity && syncPrimary) {
                deps.setPrimarySecurity(
                    key,
                    [security],
                    false,
                    security.displayName || security.symbol,
                )
            }
            return {
                status: 'applied',
                detail: syncPrimary ? label : `${label} in pane ${slots.indexOf(slot) + 1}`,
            }
        }

        case 'set_layout_symbols': {
            const entries = Array.isArray(rawCommand.symbols)
                ? rawCommand.symbols
                : Array.isArray(rawCommand.panes)
                  ? rawCommand.panes
                  : null
            if (!entries?.length) {
                return { status: 'failed', reason: 'invalid_value' }
            }
            const slots = orderedPaneSlots(layout)
            const loaded: string[] = []
            for (const entry of entries.slice(0, 8)) {
                if (!entry || typeof entry !== 'object') continue
                const row = entry as Record<string, unknown>
                const slot = resolvePaneSlot(layout, row.pane ?? row.index ?? row.position)
                if (slot == null) continue
                const security = await deps.resolveSecurity(row.exchange, row.code ?? row.symbol)
                if (!security) continue
                const key = getSecurityKey(security.exchange, security.code)
                window.securityManager?.setSecurity(security)
                deps.dispatch(
                    updateWidgetSettings({
                        id: layoutId,
                        slotId: slot,
                        settings: {
                            key,
                            expressionMode: false,
                            securities: [key],
                            displayName: security.displayName || security.symbol,
                        },
                    }),
                )
                const label = security.displayName || security.symbol || security.code
                const syncPrimary = shouldSyncPrimarySecurity(layout, slot)
                if (deps.setPrimarySecurity && syncPrimary) {
                    deps.setPrimarySecurity(
                        key,
                        [security],
                        false,
                        security.displayName || security.symbol,
                    )
                }
                loaded.push(syncPrimary ? label : `${label} (pane ${slots.indexOf(slot) + 1})`)
            }
            if (!loaded.length) {
                return { status: 'failed', reason: 'security_not_found' }
            }
            return { status: 'applied', detail: loaded.join(', ') }
        }

        default:
            return { status: 'failed', reason: 'invalid_value' }
    }
}

export function availableLayoutCodes(): string[] {
    return LAYOUT_PRESETS.map((preset) => preset.code)
}
